import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

let lenis = null;

export function initScroll(breakPoint = 992) {
  // Initialize Lenis Smooth Scroll
  if (lenis) {
    lenis.destroy();
  }

  lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    smoothTouch: false,
    touchMultiplier: 2
  });

  // Connect Lenis scroll events to ScrollTrigger updates
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);

  // 1. Hero Zoom & Splitting Walls Parallax
  const heroBg = document.querySelector('.hero-w_bg');
  const leftWall = document.querySelector('[hero-s_left]');
  const rightWall = document.querySelector('[hero-s_right]');
  const logoLink = document.querySelector('.link-logo');

  if (heroBg && leftWall && rightWall) {
    const heroTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: '.hero_scroll-area',
        start: 'top top',
        end: 'bottom bottom',
        scrub: true
      }
    });

    heroTimeline.fromTo(heroBg, {
      scale: 1,
      xPercent: 0
    }, {
      scale: 6.5,
      xPercent: -2,
      ease: 'none'
    }, 0);

    heroTimeline.fromTo(leftWall, { x: '0vw' }, { x: '-50vw', ease: 'none' }, 0);
    heroTimeline.fromTo(rightWall, { x: '0vw' }, { x: '50vw', ease: 'none' }, 0);

  }

  // 2. Gulfstream Canvas Frame Sequence, Background Video & Blueprint Mask Reveal (Scroll-Triggered)
  const jetImg = document.querySelector('.img-jet-bg'); // This is the fixed background canvas
  const blueprintEl = document.querySelector('.blueprint-bg'); // This is the blueprint background overlay
  const jetVideo = document.querySelector('.img-video-bg'); // This is the background video

  if (jetImg && blueprintEl && jetVideo) {
    const context = jetImg.getContext('2d');
    jetImg.width = 1920;
    jetImg.height = 1080;

    const frameCount = 240;
    const images = [];
    const jetSequence = { frame: 0 };

    // Pre-load all 240 frames
    // Sequence 1: first 120 frames
    for (let i = 1; i <= 120; i++) {
      const img = new Image();
      img.src = `/frames/ezgif-frame-${i.toString().padStart(3, '0')}.png`;
      images.push(img);
    }
    // Sequence 2: next 120 frames
    for (let i = 1; i <= 120; i++) {
      const img = new Image();
      img.src = `/frames2/ezgif-frame-${i.toString().padStart(3, '0')}.png`;
      images.push(img);
    }

    if (images[0]) {
      images[0].onload = render;
    }

    function render() {
      context.clearRect(0, 0, jetImg.width, jetImg.height);
      const img = images[jetSequence.frame];
      if (img && img.complete) {
        const hRatio = jetImg.width / img.width;
        const vRatio = jetImg.height / img.height;
        const ratio = Math.min(hRatio, vRatio);
        const centerShift_x = (jetImg.width - img.width * ratio) / 2;
        const centerShift_y = (jetImg.height - img.height * ratio) / 2;
        context.drawImage(
          img,
          0, 0, img.width, img.height,
          centerShift_x, centerShift_y, img.width * ratio, img.height * ratio
        );
      }
    }

    // Create the master ScrollTrigger timeline spanning the entire page scroll
    const masterScrollTL = gsap.timeline({
      scrollTrigger: {
        trigger: 'body',
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.5
      }
    });

    // Phase 1 & 2: Animate the canvas frames from index 0 to 239 over progress 0.0 -> 0.80
    masterScrollTL.to(jetSequence, {
      frame: frameCount - 1,
      snap: 'frame',
      ease: 'none',
      duration: 0.80,
      onUpdate: render
    }, 0);

    // Cross-fade: Fade canvas out and video in between progress 0.80 and 0.82
    masterScrollTL.to(jetImg, {
      opacity: 0,
      duration: 0.02,
      ease: 'none'
    }, 0.80);

    masterScrollTL.to(jetVideo, {
      opacity: 1,
      duration: 0.02,
      ease: 'none'
    }, 0.80);

    // Normal Image Shrink Mask (triggered on fleet specs section for both canvas and video)
    const maskTargets = [jetImg, jetVideo];
    gsap.fromTo(maskTargets, {
      '--mask-size': '100% 150%'
    }, {
      '--mask-size': '100% 0%',
      ease: 'none',
      scrollTrigger: {
        trigger: '.jet_scroll-area',
        start: '80% bottom',
        end: 'bottom bottom',
        scrub: true
      }
    });

    // Blueprint Show Mask (triggered on fleet specs section)
    gsap.fromTo(blueprintEl, {
      '--mask-size': '100% 0%',
      '--mask-y': '200%'
    }, {
      '--mask-size': '100% 150%',
      '--mask-y': '50%',
      ease: 'none',
      scrollTrigger: {
        trigger: '.jet_scroll-area',
        start: '80% bottom',
        end: 'bottom bottom',
        scrub: true
      }
    });
  }

  // 3. Image Parallax Suffix
  gsap.utils.toArray('[parallax="img"]').forEach((img) => {
    const parent = img.closest('[parallax="w"]');
    if (parent) {
      gsap.fromTo(img, {
        yPercent: -30
      }, {
        yPercent: 10,
        ease: 'none',
        scrollTrigger: {
          trigger: parent,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5
        }
      });
    }
  });

  // 4. Dynamic Theme Color Transitions
  const themeContainers = document.querySelectorAll('[theme]');
  if (themeContainers.length) {
    document.querySelectorAll('[bg="color"]').forEach((section) => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top 10%',
        end: 'bottom 10%',
        onEnter: () => document.body.className = 'theme_on-color',
        onLeaveBack: () => document.body.className = 'theme_on-light',
        onLeave: () => document.body.className = 'theme_on-light',
        onEnterBack: () => document.body.className = 'theme_on-color'
      });
    });
  }
}

export function getLenisInstance() {
  return lenis;
}
